import type React from 'react';
import { isValidElement } from 'react';
import { TooltipContent } from '@/components/ui/tooltip';

/** Horizontal padding (px-4 on both sides) reserved by a body cell. */
const CELL_PADDING_X = 32;

/**
 * How a column reacts when its content is wider than the column.
 *
 * - `true` — always clip at the column width (saved width, else the default width).
 * - `'whenSized'` — grow to fit the content until the column has an explicit width (resized by the
 *   user or restored from a saved preference), then clip at that width.
 * - `false` / omitted — never clip; the column grows to fit its widest cell.
 */
export type ColumnTruncateMode = boolean | 'whenSized';

/**
 * Best-effort plain-text extraction from a rendered cell, used as the tooltip of a clipped cell.
 *
 * A hover tooltip's own text is skipped: it is never part of what the cell displays, so including it
 * would both duplicate the value it describes and pull unrelated prose into the cell's tooltip.
 */
export function getNodeText(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getNodeText).join('');
  if (isValidElement<{ children?: React.ReactNode }>(node)) {
    if (node.type === TooltipContent) return '';
    return getNodeText(node.props.children);
  }
  return '';
}

/**
 * Width at which a cell's content should be clipped, or `undefined` when the column is free to grow.
 *
 * @param sizedWidth width explicitly set for the column (user resize or saved preference)
 * @param defaultWidth the column's baseline width
 */
export function getClipWidth(
  truncate: ColumnTruncateMode | undefined,
  sizedWidth: number | undefined,
  defaultWidth?: number,
): number | undefined {
  if (truncate === 'whenSized') return sizedWidth;
  if (truncate) return sizedWidth ?? defaultWidth;
  return undefined;
}

export interface CellClip {
  /** Tooltip holding the full value, since the visible text is cut off. */
  readonly title: string | undefined;
  /** Cell content, wrapped so it ellipsises at the column width when the column is clipped. */
  readonly content: React.ReactNode;
}

/**
 * Applies clipping to a rendered cell: the content is capped at `clipWidth` with an ellipsis and the
 * full text is returned as a tooltip. When `clipWidth` is undefined the content is returned as-is so
 * the column can still grow to fit it.
 *
 * The descendant rules matter because `text-overflow` only draws an ellipsis on the box that actually
 * overflows: cell content is often a `<button>` or `<span>` (sometimes nested in a flex row next to a
 * badge), which would otherwise be cut off by the wrapper with no ellipsis of its own.
 */
export function clipCell(content: React.ReactNode, clipWidth: number | undefined): CellClip {
  if (!clipWidth) return { title: undefined, content };
  return {
    title: getNodeText(content) || undefined,
    content: (
      <div className="truncate [&_*]:max-w-full [&_*]:truncate" style={{ maxWidth: `${clipWidth - CELL_PADDING_X}px` }}>
        {content}
      </div>
    ),
  };
}
